import { useDeviceId } from '@/hooks/useDeviceId';
import { setApiDeviceId } from '@/utils/ky';
import { createContext, useContext, useEffect, useState } from 'react';

// Extend Window interface to allow custom properties
declare global {
  interface Window {
    __deviceId?: string;
  }
}

// Create a context to share device ID across the app
interface DeviceIdContextType {
  deviceId: string | null;
}

const DeviceIdContext = createContext<DeviceIdContextType>({ deviceId: null });

// Hook to easily access the device ID anywhere in the app
export const useDeviceIdContext = () => useContext(DeviceIdContext);

interface DeviceIdProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that initializes device ID tracking
 * and sets up API headers globally
 */
export function DeviceIdProvider({ children }: DeviceIdProviderProps) {
  const deviceId = useDeviceId();
  const [isInitialized, setIsInitialized] = useState(false);

  // Generate fallback device ID immediately if needed
  useEffect(() => {
    if (!deviceId) {
      // If device ID is null, generate a fallback immediately to ensure one exists
      console.warn('Device ID not available, generating immediate fallback');
      const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
      // Store it for future sessions
      try {
        localStorage.setItem('device_id', fallbackId);
      } catch (e) {
        console.warn('Failed to store device ID in localStorage', e);
      }
      // Set it for the current session
      setApiDeviceId(fallbackId);
      window.__deviceId = fallbackId;
    }
  }, [deviceId]);

  // Set up API headers whenever device ID changes
  useEffect(() => {
    if (deviceId) {
      console.log('Device ID initialized:', deviceId);
      // Configure the API to include the device ID in all requests
      setApiDeviceId(deviceId);

      // Log a confirmation that the headers are set
      console.log('API headers updated with device ID');

      // Add a custom property to the window object for debugging
      window.__deviceId = deviceId;

      setIsInitialized(true);
    }
  }, [deviceId]);

  return (
    <DeviceIdContext.Provider value={{ deviceId }}>
      {children}
    </DeviceIdContext.Provider>
  );
}